import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Search, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

interface SearchOption {
  value: string;
  label: string;
}

interface SearchFormProps {
  searchBy: string;
  setSearchBy: (value: string) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  onSearch: (e: React.FormEvent) => void;
  onReload: () => void;
  isLoading: boolean;
  searchOptions: SearchOption[];
  onAutoSearch: () => void;
}

const CCM_STATUS_OPTIONS = [
  'Consent',
  'Enrolled', 
  'Deferred',
  'NANV (CS)',
  'LVOM (CS)',
  'Wrong Number',
  'Declined',
  'SMS',
  'EMAIL',
  'Follow Up',
  'Deferred (CS)',
  'Consented (In-Person)',
  'Enrolled (In-Person)',
  'Not Applicable (TTCM)',
  'No Show'
];

const RPM_STATUS_OPTIONS = [
  'No Status Assigned',
  'Declined',
  'Enrolled',
  'Deferred',
  'Not Applicable',
  'Follow Up'
];

const COMBINED_STATUS_OPTIONS = [
  'Consent',
  'Enrolled',
  'Deferred', 
  'NANV (CS)',
  'LVOM (CS)',
  'Wrong Number',
  'Declined',
  'SMS',
  'EMAIL',
  'Follow Up',
  'Deferred (CS)'
];

const SearchForm: React.FC<SearchFormProps> = ({
  searchBy,
  setSearchBy,
  searchTerm,
  setSearchTerm,
  onSearch,
  onReload,
  isLoading,
  searchOptions,
  onAutoSearch
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const isInitialMount = useRef(true);

  // Generate year options (last 100 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, i) => currentYear - i);
  
  // Month options
  const monthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Generate day options (1-31)
  const dayOptions = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  // Handle year selection
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    updateDateSearchTerm(year, selectedMonth, selectedDay);
  };

  // Handle month selection
  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateDateSearchTerm(selectedYear, month, selectedDay);
  };

  // Handle day selection
  const handleDayChange = (day: string) => {
    setSelectedDay(day);
    updateDateSearchTerm(selectedYear, selectedMonth, day);
  };

  // Update search term when year/month/day changes
  const updateDateSearchTerm = (year: string, month: string, day: string) => {
    if (year && month && day) {
      setSearchTerm(`${year}-${month}-${day}`); // Format as YYYY-MM-DD for API
      onAutoSearch(); // Auto-search when all three are selected
    } else {
      setSearchTerm('');
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      // Format date as YYYY-MM-DD for API
      const formattedDate = format(date, 'yyyy-MM-dd');
      setSearchTerm(formattedDate);
    }
  };

  const handleStatusChange = (status: string, checked: boolean) => {
    let newStatuses;
    if (checked) {
      newStatuses = [...selectedStatuses, status];
    } else {
      newStatuses = selectedStatuses.filter(s => s !== status);
    }
    setSelectedStatuses(newStatuses);
    setSearchTerm(newStatuses.join(','));
  };

  // Clear search term and selected options when search type changes (but not on initial load)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip clearing on initial mount
    }
    
    // Only clear when search type actually changes after initialization
    setSearchTerm('');
    setSelectedDate(undefined);
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setSelectedStatuses([]);
  }, [searchBy, setSearchTerm]);

  // Auto-trigger search when date is selected for DOB
  useEffect(() => {
    if (searchBy === 'dob' && selectedDate && searchTerm) {
      onAutoSearch();
    }
  }, [searchBy, selectedDate, searchTerm, onAutoSearch]);

  const renderSearchInput = () => {
    if (searchBy === 'dob') {
      return (
        <div className="flex items-center space-x-2 flex-1">
          <CalendarIcon className="h-4 w-4 text-gray-500" />
          
          <Select value={selectedMonth} onValueChange={handleMonthChange}>
            <SelectTrigger className="w-32 bg-white border-gray-300 z-10">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent className="bg-white border shadow-lg z-20">
              {monthOptions.map((month) => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedDay} onValueChange={handleDayChange}>
            <SelectTrigger className="w-20 bg-white border-gray-300 z-10">
              <SelectValue placeholder="Day" />
            </SelectTrigger>
            <SelectContent className="bg-white border shadow-lg z-20 max-h-60">
              {dayOptions.map((day) => (
                <SelectItem key={day} value={day}>
                  {day}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedYear} onValueChange={handleYearChange}>
            <SelectTrigger className="w-24 bg-white border-gray-300 z-10">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent className="bg-white border shadow-lg z-20 max-h-60">
              {yearOptions.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (searchBy === 'CCM Status') {
      return (
        <div className="flex-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start">
                Select CCM Status
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 bg-white border shadow-lg z-50">
              <div className="space-y-2">
                {CCM_STATUS_OPTIONS.map((status) => (
                  <div key={status} className="flex items-center space-x-2">
                    <Checkbox
                      id={status}
                      checked={selectedStatuses.includes(status)}
                      onCheckedChange={(checked) => handleStatusChange(status, checked as boolean)}
                    />
                    <label htmlFor={status} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {status}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    if (searchBy === 'RPM Status') {
      return (
        <div className="flex-1">
          <Select value={searchTerm} onValueChange={(value) => { setSearchTerm(value); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select RPM Status" />
            </SelectTrigger>
            <SelectContent className="bg-white border shadow-lg z-50">
              {RPM_STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    return (
      <Input
        placeholder="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="flex-1"
      />
    );
  };

  return (
    <form id="search_form_container" onSubmit={onSearch} className="flex items-center space-x-4">
      <Select value={searchBy} onValueChange={setSearchBy}>
        <SelectTrigger id="search_form_searchby_select" className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white border shadow-lg z-50">
          {searchOptions.map((option) => (
            <SelectItem id={`search_form_option_${option.value}`} key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {renderSearchInput()}
      
      <Button 
        id="search_form_submit_btn"
        type="submit" 
        className="bg-purple-600 hover:bg-purple-700 text-white"
        disabled={isLoading}
      >
        <Search className="h-4 w-4 mr-2" />
        Search
      </Button>
      
      <Button 
        id="search_form_reload_btn"
        type="button"
        onClick={onReload}
        variant="outline"
        size="icon"
        className="h-10 w-10 rounded-full"
        disabled={isLoading}
      >
        <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
      </Button>
    </form>
  );
};

export default SearchForm;